import {
  Card, CardContent, Typography, Alert, Stack, Table, TableHead, TableRow, TableCell, TableBody, Chip, Skeleton,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { PageHeader } from "../components/PageHeader";
import { Area, useAccess } from "../context/AccessContext";
import { useTenant } from "../context/TenantContext";
import { AvisoDeLeitura } from "../components/AvisoDeLeitura";

// Antes esta tela era uma matriz fixa no codigo, com papeis (viewer, editor, admin,
// superadmin) que nao existem no sistema. Agora mostra o acesso real, do mesmo
// lugar que decide: /api/access.

const AREAS: { area: Area; label: string }[] = [
  { area: "kpis", label: "Indicadores da empresa (Home)" },
  { area: "dashboards", label: "Dashboards" },
  { area: "relatorios", label: "Relatórios" },
  { area: "relatorios.reenviar", label: "Reenviar relatório" },
  { area: "notificacoes", label: "Notificações de envio" },
  { area: "sophia", label: "Sophia" },
  { area: "integracoes", label: "Integrações" },
  { area: "usuarios", label: "Usuários" },
  { area: "vendedores", label: "Vendedores" },
  { area: "logs", label: "Logs de acesso" },
  { area: "configuracoes", label: "Configurações (time UpOwl)" },
];

const ESCOPOS: Record<string, string> = {
  tenant_all: "Toda a empresa",
  rca_only: "Apenas a própria carteira (vendedor)",
  team_only: "Apenas a própria equipe",
  procurement_scope: "Compras: produtos, custos, estoque",
  collector_only: "Apenas a própria cobrança",
  customer_only: "Apenas o próprio cadastro de cliente",
  supplier_only: "Apenas os próprios pedidos de fornecedor",
  finance_scope: "Financeiro",
  none: "Nenhum",
};

export function Permissions() {
  const { tenant } = useTenant();
  const { access, loading } = useAccess();

  if (loading || !access) {
    return (
      <>
      {!loading && !access && <AvisoDeLeitura oQue="as suas permissões" />}
        <PageHeader eyebrow="Segurança" title="Permissões" subtitle="Seu acesso neste tenant" />
        {tenant ? <Skeleton variant="rounded" height={160} /> : <Alert severity="info">Nenhum tenant selecionado.</Alert>}
      </>
    );
  }

  const personas = access.is_internal ? ["Time UpOwl (interno)"] : access.personas;

  return (
    <>
      <PageHeader
        eyebrow="Segurança"
        title="Permissões"
        subtitle={<>Seu acesso em <strong>{access.tenant}</strong></>}
      />

      {!access.has_access && (
        <Alert severity="info" sx={{ borderRadius: 2 }} data-testid="permissoes-sem-perfil">
          Você não tem perfil de acesso a dados nesta empresa.
        </Alert>
      )}

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" data-testid="minhas-personas">
              <Typography variant="subtitle2" sx={{ minWidth: 90 }}>Perfil</Typography>
              {personas.length ? personas.map((p) => (
                <Chip key={p} label={nomeDaPersona(p, access.persona_catalog)} size="small" color="primary" variant="outlined" />
              )) : <Typography variant="body2" color="text.secondary">—</Typography>}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2" sx={{ minWidth: 90 }}>Dados</Typography>
              <Typography variant="body2" data-testid="meu-escopo">{ESCOPOS[access.scope] ?? access.scope}</Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ overflow: "hidden" }}>
        <Table size="small" data-testid="minhas-areas">
          <TableHead>
            <TableRow><TableCell>Área</TableCell><TableCell align="center">Acesso</TableCell></TableRow>
          </TableHead>
          <TableBody>
            {AREAS.map(({ area, label }) => (
              <TableRow key={area}>
                <TableCell>{label}</TableCell>
                <TableCell align="center">
                  {access.areas[area]
                    ? <Chip icon={<CheckRoundedIcon />} label="sim" size="small" color="success" variant="outlined" />
                    : <Typography variant="caption" color="text.secondary">—</Typography>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {access.persona_catalog && (
        <>
          <PageHeader title="Perfis disponíveis" subtitle="Catálogo de personas (config/access_control/personas.yaml)" />
          <Card variant="outlined" sx={{ overflow: "hidden" }}>
            <Table size="small" data-testid="catalogo-personas">
              <TableHead>
                <TableRow><TableCell>Perfil</TableCell><TableCell>Dados</TableCell><TableCell>Descrição</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {access.persona_catalog.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{p.name}</Typography></TableCell>
                    <TableCell>{ESCOPOS[p.scope] ?? p.scope}</TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{p.description}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </>
  );
}

function nomeDaPersona(id: string, catalogo?: { id: string; name: string }[]): string {
  const nomes: Record<string, string> = {
    seller: "Vendedor", supervisor: "Supervisor", executive: "Executivo", buyer: "Comprador",
    collector: "Cobrador", customer: "Cliente final", supplier: "Fornecedor", owner: "Owner",
    internal_admin: "Admin UpOwl",
  };
  return catalogo?.find((p) => p.id === id)?.name ?? nomes[id] ?? id;
}
